import * as React from 'react';

import { enumBookingStatus } from '@rematter/frontend-lib-api-client';
import { Button, DatePicker, TextField, uuid } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus, LucideTrash2 } from 'lucide-react';

import {
  anyDateToISOString,
  toCalendarDateOrNull,
  toISODateString,
} from '../../../../shared/utils/dateTime';
import * as state from '../../_state/bookingDetail.state';
import * as bookingFormState from '../../_state/bookingForm.state';
import { $updateDraftBooking } from '../../_state/bookingForm.state';
import { BookingRecord } from '../../types/booking';
import { SalesDetailLayout } from '../_layouts/SalesDetailLayout';
import { FilesTab } from '../_shared/FilesTab';
import { SalesOrdersMultiSelect } from '../_shared/SalesOrdersMultiSelect';

import {
  BookingPhotoRequirements,
  PhotoRequirement,
} from './BookingPhotoRequirements';

import { useBookingFiles } from '~/modules/trade/_components/bookings/hooks/useBookingFiles';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { GenericFile } from '~/modules/trade/types/file';
import { SelectContainerType } from '~/shared/components/select-fields/SelectContainerType';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectPort } from '~/shared/components/select-fields/SelectPort';
import { SelectTerminal } from '~/shared/components/select-fields/SelectTerminal';

export type BookingDetailFormState = {
  facilityId?: string;
  bookingNumber?: string;
  earlyReturnDate?: string;
  cutOffDate?: string;
  vesselArrivalDate?: string;
  vesselDepartureDate?: string;
  loadingPortId?: string;
  loadingPort?: {
    id: string;
    name: string;
    unLocode: string;
    countryCode: string;
  };
  destinationPortId?: string;
  destinationPort?: {
    id: string;
    name: string;
    unLocode: string;
    countryCode: string;
  };
  pickupTerminalId?: string;
  pickupTerminal?: { id: string; name: string; terminalCode: string };
  dropoffTerminalId?: string;
  dropoffTerminal?: { id: string; name: string; terminalCode: string };
  steamshipLine?: string;
  oceanFreight?: string;
  vesselName?: string;
  salesOrders?: {
    id: string;
    documentNumber: string;
    counterpartyReferenceNumber?: string | null;
  }[];
  salesOrderIds?: string[];
};

interface Props {
  booking: BookingRecord;
}

//
// ⚛️ ⚛️ ⚛️ BOOKING TAB BASIC INFO ⚛️ ⚛️ ⚛️
//
const BookingTabBasicInfo: React.FC<Props> = ({ booking }) => {
  const t = useTranslations();
  const toast = useToast();

  // Atoms
  const updateBookingMutation = useSetAtom(state.$updateBooking);
  const updateDraftBookingMutation = useSetAtom($updateDraftBooking);
  const { data: defaultPhotoRequirements } = useAtomValue(
    bookingFormState.$defaultPhotoRequirements,
  );
  const updateDefaultPhotoRequirements = useSetAtom(
    bookingFormState.$updateDefaultPhotoRequirements,
  );
  const [useDefaultPhotoRequirements, setUseDefaultPhotoRequirements] = useAtom(
    state.$useDefaultPhotoRequirements,
  );
  const isMissingRequired = useAtomValue(state.$bookingIsMissingRequired);

  // Other data hooks
  const bookingFileMutations = useBookingFiles();

  // State
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [sameAsPickup, setSameAsPickup] = React.useState(true);
  const [containers, setContainers] = React.useState<
    Array<{
      containerType: { id: string; name: string };
      quantity: number;
    }>
  >([]);
  const [photoRequirements, setPhotoRequirements] = React.useState<
    PhotoRequirement[]
  >([]);
  const [formData, setFormData] = React.useState<BookingDetailFormState>({});
  const [files, setFiles] = React.useState<GenericFile[]>([]);
  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, string>
  >({});

  // Callbacks
  const resetDataToRemoteBooking = React.useCallback(() => {
    if (booking) {
      setFormData({
        facilityId: booking.facilityId,
        bookingNumber: booking.bookingNumber || '',
        earlyReturnDate: anyDateToISOString(booking.earlyReturnDate),
        cutOffDate: anyDateToISOString(booking.cutOffDate),
        vesselArrivalDate: anyDateToISOString(booking.vesselArrivalDate),
        vesselDepartureDate: anyDateToISOString(booking.vesselDepartureDate),
        loadingPortId: booking.loadingPortId,
        loadingPort: booking.loadingPort,
        destinationPortId: booking.destinationPortId,
        destinationPort: booking.destinationPort,
        pickupTerminalId: booking.pickupTerminalId,
        pickupTerminal: booking.pickupTerminal,
        dropoffTerminalId: booking.dropoffTerminalId,
        dropoffTerminal: booking.dropoffTerminal,
        steamshipLine: booking.steamshipLine || '',
        oceanFreight: booking.oceanFreight || '',
        vesselName: booking.vesselName || '',
        salesOrders: booking.salesOrders,
        salesOrderIds: booking.salesOrders?.map((salesOrder) => salesOrder.id),
      });
      if (
        booking.pickupTerminalId &&
        booking.dropoffTerminalId &&
        booking.pickupTerminalId === booking.dropoffTerminalId
      ) {
        setSameAsPickup(true);
      } else {
        setSameAsPickup(false);
      }
      if (booking.containers && booking.containers.length > 0) {
        setContainers(booking.containers);
      } else {
        setContainers([]);
      }
      if (booking.photoRequirements && booking.photoRequirements.length > 0) {
        setPhotoRequirements(
          booking.photoRequirements.map((req) => ({
            id: uuid(),
            description: req.description,
            required: req.required,
          })),
        );
      } else {
        setPhotoRequirements([]);
      }
      if (booking.files && booking.files.length > 0) {
        setFiles(booking.files);
      } else {
        setFiles([]);
      }
    }
  }, [booking]);

  const handleFieldChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [field]: value,
        };
        if (field === 'pickupTerminalId' && sameAsPickup) {
          updated.dropoffTerminalId =
            (value as typeof updated.dropoffTerminalId) ?? undefined;
        }
        return updated;
      });
    };

  const addContainer = () => {
    setContainers([
      ...containers,
      {
        containerType: { id: '', name: '20ft Standard Container' },
        quantity: 1,
      },
    ]);
  };
  const removeContainer = (index: number) => {
    setContainers(containers.filter((_, i) => i !== index));
  };

  const handleDiscard = async () => {
    try {
      resetDataToRemoteBooking();
      toast.info(t.bookingDetail.actions.discardChangesSuccess);
    } catch (error) {
      console.error(error);
      toast.error(t.bookingDetail.actions.discardChangesError);
    }
  };

  const validatePhotoRequirements = () => {
    const errors: Record<string, string> = {};
    photoRequirements.forEach((req, index) => {
      if (
        req.description.trim() === '' &&
        index < photoRequirements.length - 1
      ) {
        errors[`photoRequirement_${index}`] =
          t.bookingDetail.validation.photoRequirementEmpty;
      }
    });
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(t.bookingDetail.validation.fixValidationErrors);
      return false;
    }
    return true;
  };

  const handleSaveUpdates = async () => {
    if (!booking?.id) return;
    try {
      if (!validatePhotoRequirements()) {
        return;
      }

      const updateInput = {
        id: booking.id,
        facilityId: formData.facilityId,
        documentNumber: formData.bookingNumber,
        cutOffDate: formData.cutOffDate?.toString(),
        earlyReturnDate: formData.earlyReturnDate?.toString(),
        vesselArrivalDate: formData.vesselArrivalDate?.toString(),
        vesselDepartureDate: formData.vesselDepartureDate?.toString(),
        loadingPortId: formData.loadingPortId,
        destinationPortId: formData.destinationPortId,
        pickupTerminalId: formData.pickupTerminalId,
        dropoffTerminalId: formData.dropoffTerminalId,
        steamshipLine: formData.steamshipLine,
        oceanFreight: formData.oceanFreight,
        vesselName: formData.vesselName,
        containers: containers.map((container) => ({
          containerTypeId: container.containerType.id,
          quantity: container.quantity,
        })),
        photoRequirements: photoRequirements
          .filter((reqItem) => !!reqItem.description?.trim().length)
          .map((req) => ({
            description: req.description,
            required: req.required,
          })),
        salesOrderIds: formData.salesOrders?.map((salesOrder) => salesOrder.id),
      };
      setIsUpdating(true);

      if (isMissingRequired && isDraftStatus) {
        await updateDraftBookingMutation(updateInput);
      } else {
        await updateBookingMutation(updateInput);
      }

      if (useDefaultPhotoRequirements) {
        await updateDefaultPhotoRequirements(photoRequirements);
      }

      toast.success(t.bookingDetail.actions.saveChangesSuccess);
    } catch {
      toast.error(t.bookingDetail.actions.saveChangesError);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePhotoRequirementChange = React.useCallback(
    (
      value:
        | PhotoRequirement[]
        | ((prev: PhotoRequirement[]) => PhotoRequirement[]),
    ) => {
      setPhotoRequirements(value);
      setValidationErrors({});
    },
    [],
  );

  // Effects
  React.useEffect(() => {
    resetDataToRemoteBooking();
  }, [resetDataToRemoteBooking]);

  React.useEffect(() => {
    if (sameAsPickup && formData.pickupTerminalId) {
      setFormData((prev) => ({
        ...prev,
        dropoffTerminalId: prev.pickupTerminalId,
        dropoffTerminal: prev.pickupTerminal,
      }));
    }
  }, [sameAsPickup, formData.pickupTerminalId, formData.pickupTerminal]);

  // Derived values
  const isDisabled =
    booking.status === enumBookingStatus.Completed ||
    booking.status === enumBookingStatus.Voided;
  const isDraftStatus = booking.status === enumBookingStatus.Draft;

  return (
    <SalesDetailLayout
      className="@BookingTabBasicInfo"
      footer={
        !isDisabled && (
          <div className="flex flex-shrink-0 items-center gap-3">
            <Button
              variant="outline"
              onPress={handleDiscard}
              isDisabled={isUpdating}
            >
              {t.bookingDetail.actions.discardChanges}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSaveUpdates}
              isDisabled={isUpdating}
              isLoading={isUpdating}
            >
              {isMissingRequired && isDraftStatus
                ? t.loadDetail.footer.saveDraft
                : t.bookingDetail.actions.saveChanges}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral/90">
            {t.bookingDetail.bookingInfo.title}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <SelectFacility
              value={formData.facilityId}
              onChange={handleFieldChange('facilityId')}
              label={t.bookingsView.form.fields.facility}
              isDisabled={isDisabled}
              isRequired
            />
            <TextField
              label={t.bookingDetail.bookingInfo.bookingNumber}
              value={formData.bookingNumber || ''}
              onChange={handleFieldChange('bookingNumber')}
              isDisabled={isDisabled}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SalesOrdersMultiSelect
              onSelectionChange={handleFieldChange('salesOrders')}
              selectedEntries={formData.salesOrders}
              className="col-start-1"
            />
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral">
            {t.bookingDetail.shippingDates.title}
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <DatePicker
              label={t.bookingDetail.shippingDates.earlyReturn}
              value={toCalendarDateOrNull(formData.earlyReturnDate)}
              onChange={(date) =>
                handleFieldChange('earlyReturnDate')(
                  date ? toISODateString(date) : undefined,
                )
              }
              isDisabled={isDisabled}
            />
            <DatePicker
              label={t.bookingDetail.shippingDates.cutoff}
              value={toCalendarDateOrNull(formData.cutOffDate)}
              onChange={(date) =>
                handleFieldChange('cutOffDate')(
                  date ? toISODateString(date) : undefined,
                )
              }
              isDisabled={isDisabled}
            />
            <DatePicker
              label={t.bookingDetail.shippingDates.vesselETA}
              value={toCalendarDateOrNull(formData.vesselArrivalDate)}
              onChange={(date) =>
                handleFieldChange('vesselArrivalDate')(
                  date ? toISODateString(date) : undefined,
                )
              }
              isDisabled={isDisabled}
            />
            <DatePicker
              label={t.bookingDetail.shippingDates.vesselDeparture}
              value={toCalendarDateOrNull(formData.vesselDepartureDate)}
              onChange={(date) =>
                handleFieldChange('vesselDepartureDate')(
                  date ? toISODateString(date) : undefined,
                )
              }
              isDisabled={isDisabled}
            />
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral/90">
            {t.bookingDetail.freightInfo.title}
          </h3>
          <div className="space-y-3">
            {(containers.length > 0
              ? containers
              : booking.containers || []
            ).map((container, index) => (
              <div key={index} className="grid grid-cols-2 gap-4">
                <SelectContainerType
                  label={t.bookingDetail.freightInfo.containerType}
                  value={container.containerType?.id || ''}
                  onChange={(_, containerType) => {
                    setContainers(
                      containers.map((c, i) =>
                        i === index
                          ? {
                              ...c,
                              containerType: {
                                ...c.containerType,
                                ...containerType,
                              },
                            }
                          : c,
                      ),
                    );
                  }}
                  isDisabled={isDisabled}
                />
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <TextField
                      label={t.bookingsView.form.fields.containersAmount}
                      value={container.quantity?.toString() || '1'}
                      onChange={(value) => {
                        const updated = [...containers];
                        updated[index] = {
                          ...updated[index],
                          quantity: parseInt(value) || 0,
                        };
                        setContainers(updated);
                      }}
                      isDisabled={isDisabled}
                    />
                  </div>
                  {!isDisabled && (
                    <Button
                      variant="outline"
                      size="small"
                      className="h-input size-input p-2"
                      onPress={() => removeContainer(index)}
                    >
                      <LucideTrash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {!isDisabled && (
              <Button
                variant="link"
                className="h-auto p-0 text-primary/60"
                onPress={addContainer}
              >
                <LucidePlus className="mr-1 size-4" />
                {t.bookingsView.form.buttons.addAnotherType}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SelectPort
              value={formData.loadingPortId}
              onChange={handleFieldChange('loadingPortId')}
              isDisabled={isDisabled}
              label={t.bookingsView.form.fields.loadingPort}
              isRequired
            />
            <SelectPort
              value={formData.destinationPortId}
              onChange={handleFieldChange('destinationPortId')}
              isDisabled={isDisabled}
              label={t.bookingsView.form.fields.destinationPort}
              isRequired
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SelectTerminal
              value={formData.pickupTerminalId}
              onChange={handleFieldChange('pickupTerminalId')}
              isDisabled={isDisabled}
              label={t.bookingsView.form.fields.pickupTerminal}
              portId={formData.loadingPortId}
              selectedTerminal={formData.pickupTerminal}
              isRequired
            />
            <SelectTerminal
              value={formData.dropoffTerminalId}
              onChange={handleFieldChange('dropoffTerminalId')}
              isDisabled={isDisabled}
              label={t.bookingsView.form.fields.dropoffTerminal}
              portId={formData.destinationPortId}
              selectedTerminal={formData.dropoffTerminal}
              isRequired
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <TextField
              label={t.bookingDetail.freightInfo.steamshipLine}
              value={formData.steamshipLine || ''}
              onChange={handleFieldChange('steamshipLine')}
              isDisabled={isDisabled}
              isRequired
            />
            <TextField
              label={t.bookingDetail.freightInfo.oceanFreight}
              prefix="$"
              value={formData.oceanFreight || ''}
              onChange={handleFieldChange('oceanFreight')}
              isDisabled={isDisabled}
            />
            <TextField
              label={t.bookingDetail.freightInfo.vesselName}
              value={formData.vesselName || ''}
              onChange={handleFieldChange('vesselName')}
              isDisabled={isDisabled}
              isRequired
            />
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-neutral/90">
            {t.bookingDetail.photoRequirements.title}
          </h3>

          <BookingPhotoRequirements
            validationErrors={validationErrors}
            useAsDefault={useDefaultPhotoRequirements}
            defaultPhotoRequirements={defaultPhotoRequirements}
            onUseAsDefaultChange={setUseDefaultPhotoRequirements}
            photoRequirements={photoRequirements}
            onPhotoRequirementsChange={handlePhotoRequirementChange}
            isDisabled={isDisabled}
          />
        </div>
        <div style={{ contain: 'layout' }}>
          <FilesTab
            entityId={booking.id}
            files={files}
            onFilesChange={setFiles}
            mutations={bookingFileMutations}
            title={t.bookingDetail.files.title}
            disabled={isDisabled}
          />
        </div>
      </div>
    </SalesDetailLayout>
  );
};

export default BookingTabBasicInfo;
